import { memo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ExternalLink, TrendingUp, ArrowRight } from "lucide-react";
import { TrustpilotRating } from "./TrustpilotRating";
import { Product } from "@/contexts/ComparisonContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RiskWarning } from "@/components/RiskWarning";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { trackAffiliateClick } from "@/utils/gtmTracking";
import { protectNumberGroups } from "@/utils/formatAmount";

interface FeaturedProductCardProps {
  product: Product;
  badge?: string;
  reviewCount?: number;
  stats?: {
    monthlyComparisons?: string;
    avgSavings?: string;
  };
  rateLabel?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

export const FeaturedProductCard = memo(({
  product,
  badge,
  reviewCount,
  stats,
  rateLabel,
  primaryButtonText,
  secondaryButtonText,
  secondaryButtonLink
}: FeaturedProductCardProps) => {
  return (
    <Card className="p-3 sm:p-4 md:p-6 space-y-2 sm:space-y-3 md:space-y-4 backdrop-blur-sm bg-card/95 border-2 border-primary/20 shadow-xl">
      {badge && (
        <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs sm:text-sm">
          {badge}
        </Badge>
      )}
      
      {/* Provider Logo & Rating */}
      <div className="space-y-2 sm:space-y-3">
        <div className="flex justify-center">
          <img src={product.logo} alt={product.provider} className="h-10 sm:h-12 md:h-14 w-auto object-contain max-w-full" />
        </div>
        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
          <TrustpilotRating rating={product.rating} size="md" showScore={true} showLogo={true} />
          {reviewCount && (
            <span className="text-[10px] sm:text-xs text-muted-foreground">
              ({reviewCount.toLocaleString('sv-SE')} omdömen)
            </span>
          )}
        </div>
      </div>

      {/* Key Data */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 py-2 sm:py-3 md:py-4 border-y border-border">
        <div>
          <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground line-clamp-1">
            {rateLabel || (product.product_type === 'savings' ? 'Sparränta' : 'Effektiv ränta')}
          </p>
          <p className={cn(
            "font-bold text-primary",
            product.product_type === 'savings' 
              ? "text-lg sm:text-xl md:text-2xl" 
              : "text-xl sm:text-2xl md:text-3xl"
          )}>
            {product.product_type === 'savings' 
              ? product.rate || "3,85%" 
              : `från ${product.rate || "8,95%"}`
            }
          </p>
        </div>
        <div>
          <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground line-clamp-1">
            {product.product_type === 'savings' ? 'Bindningstid' : 'Lånebelopp'}
          </p>
          <p className="text-xs sm:text-sm font-semibold text-foreground mt-1">{protectNumberGroups(product.amount)}</p>
        </div>
      </div>

      {/* Features/USPs */}
      <ul className="space-y-1.5 sm:space-y-2">
        {product.features.slice(0, 4).map((feature, idx) => (
          <li key={idx} className="flex items-start gap-1.5 sm:gap-2 text-xs sm:text-sm">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary" />
            </div>
            <span className="text-foreground leading-tight">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Stats (if provided) */}
      {stats && (
        <div className="flex gap-2 sm:gap-4 p-2 sm:p-3 bg-muted rounded-lg">
          {stats.monthlyComparisons && (
            <div className="flex-1">
              <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">Jämförelser/mån</p>
              <p className="font-bold text-foreground text-sm sm:text-base">{stats.monthlyComparisons}</p>
            </div>
          )}
          {stats.avgSavings && (
            <div className="flex-1">
              <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">Genomsnittlig besparing</p>
              <p className="font-bold text-foreground text-sm sm:text-base">{stats.avgSavings}</p>
            </div>
          )}
        </div>
      )}

      {/* CTA Buttons */}
      <div className="space-y-2 sm:space-y-3">
        <Button 
          asChild 
          size="lg" 
          className="w-full bg-primary hover:bg-primary-dark transition-all duration-300 hover:shadow-lg md:hover:-translate-y-0.5 text-sm sm:text-base px-3 sm:px-4"
          onClick={() => trackAffiliateClick({
            provider: product.provider,
            pageType: 'featured_card',
            section: 'cta'
          })}
        >
          <a href={product.link} target="_blank" rel="sponsored nofollow noopener noreferrer" className="flex items-center justify-center">
            <span className="truncate">{primaryButtonText || (secondaryButtonText ? 'Ansök hos Ordna Bolån' : 'Se erbjudande')}</span>
            <ExternalLink className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
          </a>
        </Button>
        
        {secondaryButtonText && secondaryButtonLink && (
          <Button 
            asChild 
            size="lg" 
            variant="outline"
            className="w-full border border-muted-foreground/30 text-foreground bg-transparent hover:bg-transparent hover:text-foreground hover:border-muted-foreground/50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 group text-xs sm:text-sm px-2 sm:px-3"
          >
            {secondaryButtonLink.startsWith('/') ? (
              <Link to={secondaryButtonLink} className="flex items-center justify-center w-full">
                <span className="truncate">{secondaryButtonText}</span>
                <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            ) : (
              <a href={secondaryButtonLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full">
                <span className="truncate">{secondaryButtonText}</span>
                <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </a>
            )}
          </Button>
        )}
      </div>

      {/* Warning Alert - Only show for loans and credit cards, NOT for savings */}
      {(product.product_type === 'private_loan' || 
        product.product_type === 'business_loan' ||
        product.product_type === 'credit_card') && (
        <RiskWarning />
      )}
    </Card>
  );
});
